import * as React from "react";
import {Loader} from "../components/Utils/loader/Loader";
import {UserInfo} from "./user-details-sections/UserInfo";
import {useUserDetails} from "./contexts/UserDetailsContext";
import {UserRoles} from "./user-details-sections/UserRoles";
import {UserHierarchyVisibility} from "./user-details-sections/UserHierarchyVisibility";
import {UserActivities} from "./user-details-sections/UserActivities";
import {Display} from "../components/Display";
import {VeboxDrawer} from "./components/VeboxDrawer";
import {Link, Outlet, useLocation, useNavigate} from "react-router-dom";
import {VeBoxModal} from "../components/modal/VeBoxModal";
import {RestDataLoader} from "../dashboard/widgets/unilever/RestDataLoader";
import {Button, EmptyState, icons} from "@vebox/etui-core";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import {useCommonData} from "./contexts/CommonDataProvider";
import {VeboxModels} from "../types/veboxModels";
import {VeboxUser} from "../providers/VeboxUser";
import {HierarchyError, useUserHierarchyVisibility} from "./contexts/UserVisibilityHierarchiesContext";
import {DATA_STATE} from "./contexts/UsersDashboardContext";
import {DeleteUser} from "./user-details-sections/DeleteUser";
import {RestDataStatus} from "../hooks/useRestData";
import {useTheme} from "@vebox/etui-system";
import {isValidDate} from "../Utils/DateUtils";
import {DaysWithLoginSection} from "./users-dashboard/charts/DaysWithLoginSection";
import {AllowableChanges, canEdit, getAllowableChanges} from "../components/Utils/RolesUtils";
import {ChangeUserHierarchyVisibility} from "./user-details-sections/ChangeUserHierarchyVisibility";
import {NotAvailableRolesList} from "./components/NotAvailableRolesList";
import useCurrentUser = VeboxUser.useCurrentUser;

declare var AppTranslations: AppTranslations;

export const enum ErrorType {
    NO_ERROR = "NO_ERROR",
    UPDATING_ERROR = "UPDATING_ERROR",
    CANNOT_RETRIEVE_ALLOWABLE_CHANGES_ERROR = "CANNOT_RETRIEVE_ALLOWABLE_CHANGES_ERROR",
    CANNOT_RETRIEVE_USER_VISIBILITY_ERROR = "CANNOT_RETRIEVE_USER_VISIBILITY_ERROR",
    INVALID_ROLES_FOR_HIERARCHY = "INVALID_ROLES_FOR_HIERARCHY",
}

const enum VisibilitySectionMode {
    READ_ONLY_MODE = "READ_ONLY_MODE",
    EDITING_MODE = "EDITING_MODE",
}

export const ROUNDED_BUTTON_STYLE = {width: "50px", height: "50px"}

const getUserLocale = (userLocale: string, list: VeboxModels.Locale[]): string => {
    const locale: VeboxModels.Locale = list.find((lc) => lc.name === userLocale)
    return locale ? locale.displayName : "-"
}
const formatLastActivity = (date: Date) => {
    if (isValidDate(date)) {
        const locale = window.navigator.language;
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        };
        const formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
    }
    return AppTranslations.noDataAvailable;
}
export const UserDetails: React.FC<any> = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [user, , usersAccessesByDayState] = useUserDetails();
    const loggedInUser = useCurrentUser();
    const location = useLocation();
    const [error, setError] = React.useState<ErrorType>(ErrorType.NO_ERROR);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isCurrentUser, setIsCurrentUser] = React.useState<boolean>(false);
    const [canEditThisUser, setCanEditThisUser] = React.useState<boolean>(false);
    const [imagePath, setImagePath] = React.useState("");
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [visibilitySectionMode, setVisibilitySectionMode] = React.useState<VisibilitySectionMode>(
        VisibilitySectionMode.READ_ONLY_MODE);
    const [, currentUserHierarchiesState, , , errorMessage] = useUserHierarchyVisibility();
    const [allowableChanges, setAllowableChanges] = React.useState<AllowableChanges>()
    const [allowableChangesStatus, setAllowableChangesStatus] = React.useState<DATA_STATE>(DATA_STATE.isLoading);

    const {locales, hierarchiesTree, hierarchiesTreeStatus} = useCommonData()

    const handleImageLoading = () => {
        setImageLoaded(true)
    }
    const manageError = () => {
        setError(ErrorType.NO_ERROR);
        navigate("/", {relative: "route"})
    }

    const showHideEditVisibilitySection = () => {
        setVisibilitySectionMode(
            visibilitySectionMode === VisibilitySectionMode.EDITING_MODE ? VisibilitySectionMode.READ_ONLY_MODE : VisibilitySectionMode.EDITING_MODE);
    }

    const RenderErrorMessage: React.FC = () => {
        let message = "";
        switch (error) {
            case ErrorType.CANNOT_RETRIEVE_ALLOWABLE_CHANGES_ERROR:
                message = AppTranslations.usersManagement.cannotRetrieveAllowableChangesError;
                break;
            case ErrorType.CANNOT_RETRIEVE_USER_VISIBILITY_ERROR:
                message = AppTranslations.usersManagement.cannotRetrieveUserVisibilityError;
                break;
            case ErrorType.UPDATING_ERROR:
                message = AppTranslations.usersManagement.userUpdateErrorDescription;
                break;
            case ErrorType.INVALID_ROLES_FOR_HIERARCHY:
                message = AppTranslations.usersManagement.invalidRolesForCompany;
                break;
        }
        return <>
            <EmptyState>
                <EmptyState.Icon size={144} glyph={icons.ERROR_CIRCLE}/>
                <EmptyState.SubTitle color="inherit">
                    {message}
                </EmptyState.SubTitle>
            </EmptyState>
            <Display isVisible={error === ErrorType.INVALID_ROLES_FOR_HIERARCHY}>
                <NotAvailableRolesList roleCodes={JSON.parse(errorMessage ?? "[]")}/>
            </Display>
        </>
    }

    React.useEffect(() => {
        setIsLoading(currentUserHierarchiesState === DATA_STATE.isLoading
            || allowableChangesStatus === DATA_STATE.isLoading)
    }, [currentUserHierarchiesState, allowableChangesStatus])

    React.useEffect(() => {
        if (loggedInUser && user) {
            setCanEditThisUser(canEdit(loggedInUser));
            setIsCurrentUser(loggedInUser.guid === user.guid);
        }
        if (user) {
            const imageURl = `${RestDataLoader.getBaseUrl()}v7/users/${user?.guid}/profilePicture?${(new Date().getTime())}`;
            RestDataLoader.protectedImageUrl(imageURl, "").then(setImagePath);

            getAllowableChanges(user.guid)
                .then((changes: AllowableChanges) => {
                    setAllowableChanges(changes)
                    setAllowableChangesStatus(DATA_STATE.loaded)
                })
                .catch((err) => {
                    console.error(err);
                    setAllowableChangesStatus(DATA_STATE.error)
                    setError(ErrorType.CANNOT_RETRIEVE_ALLOWABLE_CHANGES_ERROR)
                })
        }
    }, [loggedInUser, user]);


    React.useEffect(() => {
        setVisibilitySectionMode(
            (currentUserHierarchiesState !== HierarchyError.HIERARCHY_NOT_FOUND
                && hierarchiesTreeStatus === RestDataStatus.LOADED
                && hierarchiesTree.length > 0
            ) ? VisibilitySectionMode.READ_ONLY_MODE : null)
    }, [currentUserHierarchiesState, hierarchiesTreeStatus, hierarchiesTree])


    return (
        <div className={"flx-row fill-parent position-relative"}>
            <VeboxDrawer
                className={"side-menu details-menu"}
                header={<h1 className={"ellipsis"}>{user?.name}</h1>}
                defaultOpen={true}
                closable={true}
            >
                <div className={"user-info-drawer"}>
                    <div className={"flx-row flx-align-center gap-10 padding-left-25"}>
                        <Display isVisible={!imageLoaded}>
                            <div className={"image-loader-container skeleton"}/>
                        </Display>
                        {!allowableChanges?.personalData?.availableProfilePhotoEditing
                            ? <img
                                className={"round-image"}
                                src={`${imagePath}`}
                                alt={"User image"}
                                onLoad={handleImageLoading}
                                width={100}
                                height={100}
                                style={imageLoaded ? {} : {display: 'none'}}
                            />
                            : (<Link
                                replace={false}
                                to={`${location.pathname}/editPicture`}
                                state={{user}}
                                style={imageLoaded ? {position: "relative"} : {display: 'none'}}
                            >
                                {user?.guid
                                    ? <img
                                        className={"round-image"}
                                        src={`${imagePath}`}
                                        alt={"User image"}
                                        onLoad={handleImageLoading}
                                        width={100}
                                        height={100}
                                    />
                                    : null}
                                <div className="settingsRoundButtonWrapper profileEditButton">
                                    <div className="onGoogleButton">
                                        <span className="material-icons-outlined icon profileEditIcon">add_photo_alternate</span>
                                    </div>
                                </div>
                            </Link>)}
                        <div className={"flx-col"}>
                            <LabelValue
                                valueClassName={"font-size-21"} labelClassName={"font-size-21"}
                                label={AppTranslations.userCreationProcess.name}
                                value={user?.name}
                            />
                            <LabelValue
                                valueClassName={"font-size-21"} labelClassName={"font-size-21"}
                                label={AppTranslations.userCreationProcess.surname}
                                value={user?.surname}
                            />
                        </div>
                    </div>
                    <LabelValue
                        valueClassName={"font-size-16"}
                        labelClassName={"font-size-16"}
                        rootClassName={"padding-top-20"}
                        label={AppTranslations.userCreationProcess.username}
                        value={user?.username}
                    />
                    <LabelValue
                        valueClassName={"font-size-16"} labelClassName={"font-size-16"}
                        label={AppTranslations.userCreationProcess.email}
                        value={user?.email}
                    />
                    <LabelValue
                        valueClassName={"font-size-16"} labelClassName={"font-size-16"}
                        label={AppTranslations.genericWords.company}
                        value={user?.company?.name}
                    />
                    <LabelValue
                        valueClassName={"font-size-16"} labelClassName={"font-size-16"}
                        label={AppTranslations.userCreationProcess.locale}
                        value={getUserLocale(user?.locale, locales)}
                    />
                    <LabelValue
                        valueClassName={"font-size-16"} labelClassName={"font-size-16"}
                        label={AppTranslations.userCreationProcess.timezone}
                        value={user?.timezone}
                    />
                    <LabelValue
                        valueClassName={"font-size-16"} labelClassName={"font-size-16"}
                        label={AppTranslations.usersManagement.lastActivity}
                        value={formatLastActivity(new Date(user?.lastActivity))}
                    />
                </div>
            </VeboxDrawer>
            <div className={"user-management"}>
                <div className={"wrapper"}>
                    <Display isVisible={!user || isLoading || hierarchiesTreeStatus === RestDataStatus.LOADING}>
                        <Loader/>
                    </Display>
                    <Display isVisible={user && !isLoading && hierarchiesTreeStatus === RestDataStatus.LOADED}>
                        <div className={"row"}>
                            <Display isVisible={error !== ErrorType.NO_ERROR}>
                                <div className={"cover-content-container"}>
                                    <VeBoxModal>
                                        <VeBoxModal.Header
                                            title={AppTranslations.errorMessages.errorTitle}
                                            onClose={manageError}
                                        />
                                        <VeBoxModal.Body style={{minHeight: "300px", minWidth: "500px"}}>
                                            <VeBoxModal.Section style={{textAlign: "left"}}>
                                                <RenderErrorMessage/>
                                            </VeBoxModal.Section>
                                        </VeBoxModal.Body>
                                    </VeBoxModal>
                                </div>
                            </Display>
                        </div>
                        <div className={"row"}>
                            <UserInfo
                                setError={setError}
                                isCurrentUser={isCurrentUser}
                                editableSettingsData={allowableChanges?.settingsData}
                                editablePersonalData={allowableChanges?.personalData}
                            />
                            <UserRoles
                                setError={setError}
                                readOnly={allowableChanges?.rolesData ? !allowableChanges.rolesData.availableEditing : false}
                                isCurrentUser={isCurrentUser}
                                availableRoles={allowableChanges?.rolesData?.availableRoles ?? []}
                            />
                        </div>
                        <div className={"row"}>
                            <Display
                                isVisible={visibilitySectionMode === VisibilitySectionMode.READ_ONLY_MODE}
                            >
                                <UserHierarchyVisibility
                                    setError={setError}
                                    readOnly={!allowableChanges?.settingsData?.availableHierarchyVisibilityEditing}
                                    showEditVisibilitySection={showHideEditVisibilitySection}
                                />
                            </Display>
                            <Display
                                isVisible={visibilitySectionMode === VisibilitySectionMode.EDITING_MODE && allowableChanges?.settingsData?.availableHierarchyVisibilityEditing}
                            >
                                <ChangeUserHierarchyVisibility
                                    setError={setError}
                                    hideEditVisibilitySection={showHideEditVisibilitySection}
                                />
                            </Display>
                            <UserActivities/>
                        </div>
                        <div className={"row"}>
                            <DaysWithLoginSection/>
                        </div>
                        <Display isVisible={canEditThisUser && !isCurrentUser}>
                            <div className={"row"}>
                                <DeleteUser/>
                            </div>
                        </Display>
                    </Display>
                </div>
            </div>
            <Outlet/>
        </div>
    );
}

type LabelValueProps = {
    label: string,
    value: string,
    valueClassName?: string,
    labelClassName?: string
    rootClassName?: string
}

const LabelValue: React.FC<LabelValueProps> = (props) => {
    return (
        <p className={"label-value " + (props?.rootClassName ?? "")}>
            <span className={"padding-bottom-3 " + props?.labelClassName ?? ""}>{props.label}</span>
            <span className={"padding-bottom-3 ellipsis " + (props?.valueClassName ?? "")}>{props.value}</span>
        </p>
    );
}

export const EditUserImage: React.FC = () => {

    const navigate = useNavigate()
    const location = useLocation()

    const [user] = useUserDetails()

    const imageInput = React.useRef<HTMLInputElement>(null);
    const [selectedImage, setSelectedImage] = React.useState<File>();
    const [isSendingImage, setIsSendingImage] = React.useState<boolean>(false)
    const close: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (e.target === e.currentTarget) {
            navigate("..", {replace: true, state: location.state.user})
        }
    }
    const closeButtons = () => {
        setIsSendingImage(false)
        navigate("..", {replace: true, state: location.state.user})
    }

    const sendImage = () => {
        const data = new FormData()
        setIsSendingImage(true)

        data.append("image", selectedImage)
        const now = new Date()
        RestDataLoader.fetchWithAuthentication(
            `${RestDataLoader.getBaseUrl()}v7/users/${user?.guid}/profilePicture`,
            RestDataLoader.getCurrentToken(),
            {
                method: "PUT",
                body: data
            }
        )
            .then(response => response.json())
            .then(() => {
                const imageURl = `${RestDataLoader.getBaseUrl()}v7/users/${user?.guid}/profilePicture?${(new Date().getTime())}`;
                RestDataLoader.protectedImageUrl(imageURl, "").then(location.state.setUserImage);
                closeButtons()
            })
            .catch(closeButtons)
    }

    const imageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const onClickUpload = () => {
        imageInput?.current?.click?.()
    }

    const removeImage = () => {
        setSelectedImage(null);
    }

    React.useEffect(() => {
        if (imageInput?.current) {
            imageInput.current.onchange = imageChange
        }
    }, [imageInput?.current])

    return (
        <>
            <VeBoxModal.Backdrop style={{position: "absolute", top: 0, left: 0}} onClick={close}>
                <VeBoxModal style={{width: 400}}>
                    <VeBoxModal.Header title={AppTranslations.usersManagement.userSettingsSelectImage}/>
                    <VeBoxModal.Section className={"edit-picture-container"}>
                        <Display isVisible={!selectedImage}>
                            <Button
                                className={"addImage"}
                                shape={"circle"}
                                variant={"secondary"}
                                onClick={onClickUpload}
                                styles={{root: {width: "50px", height: "50px"}}}
                                iconProps={{symbol: 'upload_file', weight: '400'}}
                            />
                        </Display>
                        <Display isVisible={!!selectedImage}>
                            <img
                                id={"new-user-image-preview"}
                                height={180}
                                width={180}
                                className={"preview round-image"}
                                src={selectedImage ? URL.createObjectURL(selectedImage) : ""}
                                alt={"user image"}
                            />
                        </Display>
                        <Display isVisible={!!selectedImage && !isSendingImage}>
                            <Button
                                className={"removeImage"}
                                onClick={removeImage}
                                variant={"strong"}
                                shape={"circle"}
                                styles={{root: ROUNDED_BUTTON_STYLE}}
                                iconProps={{symbol: 'delete', weight: '300'}}
                            />
                        </Display>
                        <Display isVisible={isSendingImage}>
                            <div className={"image-loader"}>
                                <Loader relativelyCentered={true}/>
                            </div>
                        </Display>
                    </VeBoxModal.Section>
                    <Display isVisible={!isSendingImage}>
                        <VeBoxModal.StandardActions
                            onSubmit={sendImage}
                            canSubmit={!!selectedImage}
                            onCancel={closeButtons}
                        />
                    </Display>
                </VeBoxModal>
            </VeBoxModal.Backdrop>
            <input
                type={"file"}
                name={"image"}
                accept={"image/*"}
                ref={imageInput}
                style={{display: "none"}}
            />
        </>
    );
}
